import React, { memo, useMemo } from 'react';
import styled from 'styled-components';
import type { LandingArchiveCandidate, LandingArchiveGroup } from '../../../shared/util/landingArchiveIndex';
import { LANDING_COLUMN_GAP2, splitExhibitionVenueIndexTwoColumns } from '../../../shared/util/exhibitionVenueIndex';

export interface ExhibitionVenueIndexMobileProps {
  groups: LandingArchiveGroup[];
  activeVenueIds: ReadonlySet<string>;
  onVenueActivate: (candidate: LandingArchiveCandidate) => void;
}

interface VenueRowProps {
  candidate: LandingArchiveCandidate;
  isActive: boolean;
  onVenueActivate: (candidate: LandingArchiveCandidate) => void;
}

const VenueRow = memo<VenueRowProps>(({ candidate, isActive, onVenueActivate }) => (
  <VenueItem
    onClick={(event) => {
      event.stopPropagation();
      onVenueActivate(candidate);
    }}
    role="button"
    tabIndex={0}
    aria-pressed={isActive}
    onKeyDown={(event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
        onVenueActivate(candidate);
      }
    }}
  >
    <VenueName $isActive={isActive}>{candidate.username}</VenueName>
  </VenueItem>
));

VenueRow.displayName = 'VenueRow';

const ExhibitionVenueIndexMobile: React.FC<ExhibitionVenueIndexMobileProps> = ({
  groups,
  activeVenueIds,
  onVenueActivate,
}) => {
  const columns = useMemo(
    () => splitExhibitionVenueIndexTwoColumns(groups),
    [groups],
  );

  return (
    <IndexContainer>
      <ColumnList>
        {columns.map((column, columnIndex) => (
          <Column key={`exhibition-mobile-column-${columnIndex}`}>
            {column.map((group) => (
              <GroupSection key={group.letter}>
                {group.label ? <GroupLabel>{group.label}</GroupLabel> : null}
                {group.candidates.map((candidate) => (
                  <VenueRow
                    key={candidate.aui}
                    candidate={candidate}
                    isActive={activeVenueIds.has(candidate.aui)}
                    onVenueActivate={onVenueActivate}
                  />
                ))}
              </GroupSection>
            ))}
          </Column>
        ))}
      </ColumnList>
    </IndexContainer>
  );
};

const IndexContainer = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ColumnList = styled.div`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: ${LANDING_COLUMN_GAP2};
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const GroupSection = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
`;

const GroupLabel = styled.div`
  width: 100%;
  ${({ theme }) => theme.typography.Footer_02};
  color: ${({ theme }) => theme.colors.color_Gray_04};
  line-height: 1;
`;

const VenueItem = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
`;

const VenueName = styled.div<{ $isActive?: boolean }>`
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 3px 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  ${({ theme }) => theme.typography.Body_04};
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.color_Gray_02 : theme.colors.color_Gray_03};
  text-decoration: ${({ $isActive }) => ($isActive ? 'underline' : 'none')};
`;

export default ExhibitionVenueIndexMobile;
